import { consume } from '@lit/context'
import { HostAriaLabelMixin, SchmancyElement } from '@schmancy/mixins/index'
import { SchmancySurfaceTypeContext } from '@schmancy/primitives/surface/context'
// The reason line is a <schmancy-typography>; a row rendered without the
// library barrel loaded (a test, a consumer importing this file alone) would
// otherwise paint it as an unknown inline element.
import '@schmancy/primitives/typography/typography'
import { TSurfaceColor } from '@schmancy/types/surface'
import { css, html } from 'lit'
import { customElement, property, queryAssignedElements } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { when } from 'lit/directives/when.js'
import { filter, fromEvent, takeUntil } from 'rxjs'

@customElement('schmancy-list-item')
export class SchmancyListItem extends HostAriaLabelMixin(SchmancyElement) {
	static styles = [
		css`
			/* The hover and selected tints travel to the row as a custom
			   property instead of painting the host directly. The cascade sorts
			   encapsulation context above specificity, and for normal
			   declarations the outer tree wins on the host — so a consumer's
			   background utility on <schmancy-list-item> silently defeated a
			   :host() background-color however specific it was, and selection
			   showed nothing. A custom property is not a property any consumer
			   background utility sets, and the row it paints is shadow-internal
			   and so out of the outer tree's reach entirely. Overriding
			   --schmancy-list-item-state-layer on the host is the supported way
			   to theme these states. */
			:host {
				display: block;
				border-radius: var(--schmancy-radius-lg, 0.625rem); /* mirrors --radius-lg (rounded-lg) */
				--schmancy-list-item-state-layer: transparent;
				transition:
					box-shadow 300ms ease,
					transform 200ms cubic-bezier(0.34, 1.56, 0.64, 1);
			}
			:host(:hover:not([readonly]):not([disabled])) {
				--schmancy-list-item-state-layer: color-mix(in srgb, var(--schmancy-sys-color-surface-on) 8%, transparent);
				box-shadow: 0 2px 8px -4px color-mix(in srgb, var(--schmancy-sys-color-primary-default) 10%, transparent);
			}
			:host(:active:not([readonly]):not([disabled])) {
				transform: scale(0.98);
				transition-duration: 100ms;
			}

			/* The disabled dim is a translucent text colour, not opacity on the
			   row. Opacity establishes a stacking context whose alpha no
			   descendant can climb back out of, and this row's disabled state has
			   one descendant that must stay readable — the reason line, which is
			   the entire point of disabling it visibly rather than hiding it. A
			   colour inherits through the flattened tree into slotted content the
			   same way the row's own text-sm already does, and the reason line
			   escapes it by naming its own colour. The alpha is the house
			   disabled token either way, so a disabled row and a disabled button
			   dim by the same amount.

			   currentColor, not the surface-on token: in the color property it
			   resolves to the colour the row would otherwise have inherited, so
			   a consumer who tinted this row keeps their hue and only loses
			   strength — and a row rendered outside any schmancy-theme still
			   dims, where an unset token would have made the whole declaration
			   invalid at computed-value time and left it undimmed. */
			:host([disabled]) {
				color: color-mix(
					in srgb,
					currentColor calc(var(--schmancy-sys-state-opacity-disabled, 0.38) * 100%),
					transparent
				);
			}
			:host([selected]) {
				--schmancy-list-item-state-layer: color-mix(
					in srgb,
					var(--schmancy-sys-color-secondary-container) 30%,
					transparent
				);
				box-shadow: 0 0 10px -3px color-mix(in srgb, var(--schmancy-sys-color-secondary-default) 12%, transparent);
			}
			@media (prefers-reduced-motion: reduce) {
				:host {
					transition: box-shadow 200ms ease;
				}
				:host(:active:not([readonly])) {
					transform: none;
				}
			}
		`,
	]
	@consume({ context: SchmancySurfaceTypeContext, subscribe: true })
	@property()
	variant: TSurfaceColor

	@property({ type: Boolean, reflect: true })
	rounded: boolean

	@property({ type: Boolean, reflect: true }) readonly: boolean

	@property({ type: Boolean, reflect: true }) selected: boolean = false

	/**
	 * The row is present and announced but cannot be activated. Distinct from
	 * `readonly`, which strips the row's role and tab stop for a list that is
	 * pure display: a disabled row keeps both, because a user has to be able to
	 * find the thing they cannot do in order to be told why. `aria-disabled`
	 * rather than the `disabled` attribute for the same reason — a natively
	 * disabled control leaves the tab order and is unreachable to a screen
	 * reader driving by keyboard (WCAG 2.2 AA), which is exactly the state a
	 * reason line exists to prevent.
	 */
	@property({ type: Boolean, reflect: true }) disabled: boolean = false

	/**
	 * Why the row is unavailable. Rendered as a second line under the row's own
	 * content and wired as the row's accessible description, so the explanation
	 * reaches a screen reader rather than only a sighted reader. Rendered only
	 * while `disabled` — a row that can be activated has nothing to explain.
	 */
	@property({ type: String }) reason: string = ''

	/**
	 * Drops the row's own padding so a consumer supplies it directly on
	 * slotted content instead — for a slotted layout that needs to own its
	 * own padding box rather than sit inside this element's default one.
	 */
	@property({ type: Boolean, reflect: true }) flush: boolean = false

	/**
	 * Where the leading and trailing slots sit against the row's content, named
	 * for the CSS property it sets on the row. `center` (the default) centres
	 * them, right for a one-line row. `start` puts them on the content's first
	 * line, for a row whose content wraps: a timestamp or an icon then sits
	 * beside the title instead of floating to the middle of a three-line row.
	 * The row lives in this shadow root, so no class on the host can reach it.
	 */
	@property({ attribute: 'align-items', reflect: true }) alignItems: 'center' | 'start' = 'center'

	/**
	 * Role the activatable row publishes. `button` is right for a standalone
	 * clickable row and for a row inside a plain list; a composite widget that
	 * owns its rows needs a different one — a `role="menu"` requires
	 * `menuitem` children, a `role="listbox"` requires `option`. The row lives
	 * in this shadow root and cannot see the container that owns it, so the
	 * container is what names the role its own semantics require.
	 */
	@property({ attribute: 'row-role' }) rowRole: 'button' | 'menuitem' | 'option' = 'button'

	/**
	 * Accessible name for the row — the same name as `aria-label` on the host,
	 * read and written through it, so whichever spelling arrived last is the one
	 * the row carries. It lands on the element carrying the row's role, because
	 * that is the element the user activates and the element `{role, name}`
	 * addresses.
	 *
	 * `label` is observed as an attribute in its own right, because the property
	 * is called `label` and `label="…"` is therefore the spelling markup reaches
	 * for — an attribute an element does not observe is inert with no error
	 * anywhere, so such a row kept the name its own CONTENT computes: a
	 * different string per row, churning as the row's own counts do.
	 */
	@property({ attribute: 'label' })
	get label(): string | null {
		return this.ariaLabel
	}
	set label(value: string | null) {
		this.ariaLabel = value
	}

	/**
	 * ARIA state a trigger sets on this host — a popup relationship and its open
	 * state. The host itself is a roleless generic node, or the `listitem` a list
	 * owns, so assistive technology reads neither of these where the consumer
	 * writes them; they are only meaningful on the row, the node that actually
	 * carries `rowRole`. Declared as reactive properties (not read off the host at
	 * render time) because a consumer's Lit attribute binding on this host
	 * schedules no update here on its own.
	 *
	 * Unset on every existing consumer, which renders exactly as before.
	 */
	@property({ attribute: 'aria-haspopup' })
	public override ariaHasPopup: 'true' | 'false' | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog' | null = null

	@property({ attribute: 'aria-expanded' })
	public override ariaExpanded: 'true' | 'false' | null = null

	@queryAssignedElements({
		slot: 'leading',
		flatten: true,
	})
	private leading!: HTMLElement[]

	@queryAssignedElements({
		slot: 'trailing',
		flatten: true,
	})
	private trailing!: HTMLElement[]

	protected get imgClasses(): string[] {
		return ['h-4', 'w-4', 'sm:h-5', 'sm:w-5', 'object-contain']
	}

	// A div with role="button" fires no `click` on Enter/Space the way a real
	// <button> does — every consumer (schmancy-menu-item, select lists, pickers)
	// wires only `@click`, so keyboard users could focus but never activate. Dispatch the
	// same click event a pointer would, once, so it reaches exactly the one
	// activation path every consumer already listens on.
	private onKeydown = (e: KeyboardEvent): void => {
		if (this.readonly || this.disabled) return
		if (e.key !== 'Enter' && e.key !== ' ') return
		// Held key: fire once only (auto-repeat should not double-activate a row).
		if (e.repeat) return
		// Activate only when the row itself holds focus. composedPath()[0] is the
		// deepest event target — this row, or a slotted control when one holds
		// focus instead, piercing that control's own shadow root to reach it.
		// Identity against the row rather than a test for which kind of control:
		// a field, a drag handle, an action button and an editable region are all
		// controls whose press is their own, and a row that takes one also
		// cancels it, since the `preventDefault` below suppresses the default the
		// control was pressed for.
		if (e.composedPath()[0] !== e.currentTarget) return
		e.preventDefault()
		this.dispatchEvent(new MouseEvent('click', { bubbles: true, composed: true, cancelable: true }))
	}

	connectedCallback(): void {
		super.connectedCallback()
		// Capture phase on the host, so the click dies before it reaches the row
		// it targets and before any bubble-phase listener a wrapper or a consumer
		// put on this element or an ancestor of it — a disabled row that still
		// ran the consumer's own @click would be disabled in appearance only.
		// pointer-events:none would be simpler and is wrong: the click would then
		// land on whatever sits beneath the row, which inside an open menu is the
		// overlay that dismisses on it.
		fromEvent<MouseEvent>(this, 'click', { capture: true })
			.pipe(
				filter(() => this.disabled),
				takeUntil(this.disconnecting),
			)
			.subscribe(e => {
				e.preventDefault()
				e.stopImmediatePropagation()
			})

		// The <ul> lives in schmancy-list's shadow root and this host is what
		// gets slotted into it, so the host — not anything this component
		// renders — is the element the list owns as its item. A row rendered
		// standalone (a lone clickable summary) claims nothing: an orphan
		// listitem with no list to belong to is worse than a plain button.
		if (!this.hasAttribute('role') && this.closest('schmancy-list')) this.setAttribute('role', 'listitem')
	}

	firstUpdated() {
		this.leading?.forEach(el => {
			if (el.tagName === 'IMG') el.classList.add(...this.imgClasses)
		})
		this.trailing?.forEach(el => {
			if (el.tagName === 'IMG') el.classList.add(...this.imgClasses)
		})
	}

	render() {
		const classes = {
			'w-full flex gap-2 sm:gap-3 min-h-[36px] sm:min-h-[40px] text-sm': true,
			'items-center': this.alignItems !== 'start',
			'items-start': this.alignItems === 'start',
			'py-1 px-2 sm:px-3': !this.flush,
			// rounded-[inherit] takes the host's radius verbatim, including a
			// consumer's own corner utility, so the tint never squares off the
			// corners the host is drawn with.
			'bg-[var(--schmancy-list-item-state-layer)] rounded-[inherit] transition-colors duration-200 ease-in-out': true,
			'focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:z-1 outline-secondary-default outline-hidden': true,
			'cursor-pointer': !this.readonly && !this.disabled,
			'cursor-not-allowed': this.disabled,
		}

		// A row role is claimed only when the row is activatable — exactly
		// `:not([readonly])`, the same condition already gating the pointer
		// cursor, the tab stop, and the Enter/Space handler above — so a static
		// list never announces its rows as pressable. `disabled` is deliberately
		// NOT part of that condition: it keeps the role and the tab stop and
		// carries `aria-disabled` instead, so the row stays in the tree to be
		// found and told why. The listitem half of the pair is on the host (see
		// connectedCallback), which is what the list's <ul> actually owns.
		//
		// The reason line lives in this shadow root rather than in a wrapper's,
		// because `aria-describedby` is an IDREF and does not cross a shadow
		// boundary — a reason rendered by `schmancy-menu-item` around this row
		// could be read but never referenced.
		return html`<div
			role=${ifDefined(this.readonly ? undefined : this.rowRole)}
			aria-label=${ifDefined(this.ariaLabel || undefined)}
			aria-haspopup=${ifDefined(this.ariaHasPopup ?? undefined)}
			aria-expanded=${ifDefined(this.ariaExpanded ?? undefined)}
			aria-current=${ifDefined(this.selected ? 'true' : undefined)}
			aria-disabled=${ifDefined(this.disabled ? 'true' : undefined)}
			aria-describedby=${ifDefined(this.disabled && this.reason ? 'reason' : undefined)}
			.tabIndex=${this.readonly ? -1 : 0}
			class=${this.classMap(classes)}
			@keydown=${this.onKeydown}
		>
			<slot name="leading"></slot>
			<div class="flex-1 min-w-0"><slot></slot>${when(
					this.disabled && this.reason,
					() => html`<schmancy-typography id="reason" type="label" token="xs" class="text-surface-on-variant"
						>${this.reason}</schmancy-typography
					>`,
				)}</div>
			<slot name="trailing"></slot>
		</div>`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-list-item': SchmancyListItem
	}
}
